if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLDivElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.tabPanels = this.querySelector('.tabs__panels');
        this.tabBtns = this.querySelectorAll('.tabs__btn');
        this.currentIndex = 0;
        this.cardsRequests = new Map();
        this.init();
      }

      init() {
        this.tabBtns.forEach((btn) => {
          btn.addEventListener('click', this.onClick.bind(this));
          // 24 Oct 2026 speed fix (Brands page): a lazy tab starts loading its cards as soon as the shopper
          // points at, touches or focuses its button, so most taps find the cards already loaded.
          const prefetch = () => this.loadCards(Number(btn.dataset.index)).catch(() => {});
          btn.addEventListener('pointerenter', prefetch);
          btn.addEventListener('pointerdown', prefetch);
          btn.addEventListener('focus', prefetch);
        });

        if (Shopify.designMode) {
          this.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId != this.sectionId) return;
            let { target } = e;
            const index = Number(target.dataset.index);

            this.setActiveTab(index);
          });
        }
      }

      onClick(e) {
        e.preventDefault();
        const { target } = e;
        const button = target.closest('button');
        const index = Number(button.dataset.index);
        this.setActiveTab(index);
      }

      setActiveTab(index) {
        if (this.currentIndex === index) return;

        const currentItem = this.tabBtns[this.currentIndex];
        const newBtn = this.tabBtns[index];

        currentItem.classList.remove('active');
        newBtn.classList.add('active');
        this.currentIndex = index;

        // 24 Oct 2026 speed fix (INP): the tap only switches the button; building the new panel
        // (12 product cards cloned from a <template>) runs after the next paint, so the screen reacts
        // at once instead of after ~1s of work. A newer tap cancels an older pending switch.
        const token = (this.switchToken = (this.switchToken || 0) + 1);
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (token !== this.switchToken) return;
            this.showPanel(index, token);
          }, 0);
        });
      }

      /**
       * 24 Oct 2026 speed fix (Brands page): HTML of a lazy tab's cards (a <template> with data-cards-url),
       * fetched once and shared by prefetch and open. Null for a tab whose cards came with the page.
       */
      loadCards(index) {
        const template = this.tabPanels.querySelector(`template[data-index="${index}"][data-cards-url]`);
        if (!template) return Promise.resolve(null);

        if (!this.cardsRequests.has(index)) {
          const request = fetch(template.dataset.cardsUrl)
            .then((response) => {
              if (!response.ok) throw new Error(`Tab cards request failed: ${response.status}`);
              return response.text();
            })
            // Same ids as a server-rendered tab: this section's id and the tab's block id.
            .then((html) =>
              html
                .split('featured-collection-tab-cards')
                .join(this.sectionId)
                .split('__FC_BLOCK__')
                .join(template.dataset.blockId)
            );
          request.catch(() => this.cardsRequests.get(index) === request && this.cardsRequests.delete(index));
          this.cardsRequests.set(index, request);
        }
        return this.cardsRequests.get(index);
      }

      /** Puts the fetched cards into the panel before it joins the page, so the slider starts with them. */
      fillCards(panel, template, html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const wrapper = doc.body.firstElementChild; // <div id="shopify-section-…" class="shopify-section">
        const list = panel.querySelector('.featured-collection__items');
        const slider = panel.querySelector('featured-collection');
        const isSlider = slider && slider.dataset.enableSlider === 'true';
        Array.from(wrapper ? wrapper.children : []).forEach((column) => {
          if (!isSlider) column.classList.remove('swiper-slide');
          if (template.dataset.colorInherit === 'true') {
            const card = column.querySelector('.product-card');
            if (card) card.classList.add('color-inherit');
          }
          list.appendChild(column);
        });
      }

      async showPanel(index, token) {
        let newTab = this.tabPanels.querySelector(`.tabs__panel[data-index="${index}"]`);

        if (!newTab) {
          const template = this.tabPanels.querySelector(`template[data-index="${index}"]`);
          const panel = template.content.cloneNode(true).firstElementChild;

          if (template.dataset.cardsUrl) {
            let html;
            try {
              html = await this.loadCards(index);
            } catch (error) {
              console.error(error);
              // The cards can't load: open the collection page instead (same as a failed quick view).
              if (token === this.switchToken) window.location.href = template.dataset.cardsUrl.split('?')[0];
              return;
            }
            // A newer tap won while the cards loaded.
            if (token !== undefined && token !== this.switchToken) return;
            newTab = this.tabPanels.querySelector(`.tabs__panel[data-index="${index}"]`);
            if (!newTab) this.fillCards(panel, template, html);
          }

          if (!newTab) {
            newTab = panel;
            this.tabPanels.appendChild(newTab);
          }
        }

        // Hidden only now, so the previous tab stays on screen while a lazy tab loads.
        this.tabPanels.querySelectorAll('.tabs__panel').forEach((panel) => {
          panel.hidden = panel !== newTab;
        });

        const translateY = !FoxTheme.a11y.shouldAnimate() ? 0 : 2.5;

        FoxTheme.Motion.animate(newTab, { transform: `translateY(${translateY}rem)`, opacity: 0.01 }, { duration: 0 });

        newTab.hidden = false;
        this.fixBlurryImagesOnSafari(newTab);

        setTimeout(() => {
          FoxTheme.Motion.animate(
            newTab,
            { transform: 'translateY(0)', opacity: 1 },
            { duration: 0.5, delay: this.animationDelay, easing: [0, 0, 0.3, 1] }
          );
        }, 50);

        this.dispatchEvent(
          new CustomEvent('tabChange', {
            bubbles: true,
            detail: { selectedIndex: index, selectedTab: newTab }
          })
        );
      }

      /**
       * Fix for Safari 15.6: force images to reload with correct size after becoming visible
       * @param {*} container
       * @returns
       */
      fixBlurryImagesOnSafari(tab) {
        const isOldSafari =
          /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && navigator.userAgent.includes('Version/15.');

        if (!isOldSafari) return;

        if (tab.classList.contains('images-loaded')) return;
        tab.classList.add('images-loaded');

        const images = tab.querySelectorAll('img[srcset]');
        images.forEach((img) => {
          const src = img.currentSrc || img.src;
          img.srcset = img.srcset; // Trick to trigger evaluation.
          img.src = ''; // Force reload.
          img.src = src;
        });
      }
    },
    { extends: 'div' }
  );
}
